// Error message parsing for content submission

/**
 * Known error patterns from the legacy backend mapped to user-friendly messages.
 * The keys are regex patterns tested against the raw error message.
 */
const CONTENT_ERROR_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  message: string | ((match: RegExpMatchArray) => string);
}> = [
  // YouTube content type mismatches — specific patterns before general ones
  {
    pattern: /youtube\s*video,?\s*not\s*a?\s*youtube\s*short/i,
    message:
      "You submitted a YouTube Shorts link, but this deliverable requires a standard YouTube video. Please submit a full YouTube video URL (e.g. youtube.com/watch?v=...) instead.",
  },
  {
    pattern: /youtube\s*short,?\s*not\s*a?\s*youtube\s*video/i,
    message:
      "You submitted a standard YouTube video link, but this deliverable requires a YouTube Short. Please submit a YouTube Shorts URL (e.g. youtube.com/shorts/...) instead.",
  },
  {
    pattern: /please\s+submit\s+a\s+([\w\s]+?),?\s*not\s+a\s+([\w\s]+)/i,
    message: (match: RegExpMatchArray) => {
      const expected = (match[1] ?? "").trim();
      const received = (match[2] ?? "").trim();
      return `You submitted a ${received} link, but this deliverable requires a ${expected}. Please submit the correct content type.`;
    },
  },
  {
    pattern: /content.?field.?validation/i,
    message:
      "Content validation failed. Please check your submission and try again.",
  },
  // Branded content tag required (TikTok / Instagram)
  {
    pattern: /not\s+properly\s+tagged\s+as\s+Branded\s+Content/i,
    message:
      "Your post is not properly tagged as Branded Content, as required in the terms. Please use the invite link above to tag it before submitting.",
  },
  // TikTok pattern must come before the general campaign link pattern (more specific)
  {
    pattern: /tiktok.*not.*linked|not.*linked.*tiktok/i,
    message:
      "This TikTok video is not linked to the campaign. Please use the branded content toggle in TikTok to link it before submitting.",
  },
  {
    pattern: /not.?linked.?to.?(the\s+)?campaign/i,
    message:
      "This content is not linked to the campaign. Please ensure the post is associated with the correct campaign before submitting.",
  },
  // TikTok Paid Partnership (Spark Ads / TTO) order was never created on the
  // brand's side, so the deliverable can't be submitted. Brand-side setup issue,
  // not the creator's. The "does not have an order_id" text is unique to this case.
  {
    pattern: /does\s+not\s+have\s+an\s+order_id/i,
    message:
      "This deliverable requires a TikTok Paid Partnership (Spark Ads) setup that the brand hasn't finished on their side yet, so it can't be submitted right now. This is not a problem with your post or your TikTok account. Please contact the brand or your point of contact, or reach out to support with the reference code shown below.",
  },
  {
    pattern: /invalid.?url|url.?is.?not.?valid|invalid.?link/i,
    message: "Invalid post link. Please enter a valid URL and try again.",
  },
  {
    pattern: /duplicate.?content|already.?submitted|already.?been.?submitted/i,
    message:
      "This content has already been submitted. Please submit different content.",
  },
  {
    pattern: /invalid\s+externalPostProjectId|invalid response for project/i,
    message:
      "We're having trouble loading your content requirements. Please try again in a few minutes. If the problem persists, contact the brand or your campaign manager.",
  },
  {
    pattern: /circuit.?breaker.?open|system.?unavailable/i,
    message:
      "The service is temporarily unavailable. Please try again in a few minutes.",
  },
  {
    pattern: /unable.?to.?process|process.?your.?request/i,
    message:
      "We were unable to process your submission. Please double-check that your link is correct and publicly accessible, then try again. If this keeps happening, contact support with the reference code shown below.",
  },
  {
    pattern: /file.?too.?large|exceeds.?max|file.?size/i,
    message:
      "The file is too large. Please reduce the file size and try again.",
  },
  {
    pattern: /unsupported.?file|invalid.?file|file.?type/i,
    message:
      "This file type is not supported. Please upload a supported image or video format.",
  },
  {
    pattern: /(session|token).?expired|expired\s+(session|token)/i,
    message: "Your session has expired. Please refresh the page and try again.",
  },
  // Account mismatch — must be before permission pattern to avoid false positives
  // from HTTP headers (e.g. "permissions-policy") in raw error bodies.
  {
    pattern:
      /(?:user\s*)?account\s*\(?([\w.]+)\)?\s*(?:for the submitted link\s+)?does\s+not\s+match\s+(?:the\s+)?(?:user\s*)?account\s*\(?([\w.]+)\)?/i,
    message: (match: RegExpMatchArray) => {
      const submitted = match[1] ?? "unknown";
      const required = match[2] ?? "unknown";
      return `The social account (@${submitted}) for the submitted link does not match the account listed in your brief (@${required}). Please submit a link from the correct account.`;
    },
  },
  {
    pattern: /permission|not.?authorized|unauthorized|forbidden/i,
    message:
      "You don't have permission to submit this content. Please contact support.",
  },
  {
    pattern: /timeout|timed?\s*out/i,
    message:
      "The request timed out. Please check your connection and try again.",
  },
  {
    pattern: /network\s*error|fetch.?failed|failed.?to.?fetch/i,
    message:
      "A network error occurred. Please check your internet connection and try again.",
  },
];

/**
 * Strip known wrapper prefixes added by tRPC and GraphQL error handling
 * to expose the underlying error message for pattern matching.
 *
 * Error messages arrive in the format:
 *   "Failed to submit content: GraphQL errors: <actual message>"
 */
export function stripErrorPrefixes(message: string): string {
  return (
    message
      .replace(/^Failed to [^:]+:\s*/i, "")
      .replace(/^GraphQL errors?:\s*/i, "")
      // Strip appended JSON request/response bodies from GraphQL client errors
      // e.g. "Your post is not tagged...: {"response":{...},"request":{...}}"
      .replace(/:\s*\{"response":\{.*$/s, "")
      .trim()
  );
}

export type ContentSubmissionError = {
  message: string;
  showReference: boolean;
};

/**
 * Convert a raw technical error message from the backend into a user-friendly message.
 * Strips tRPC/GraphQL wrapper prefixes before matching known patterns.
 * Falls back to a generic message if no known pattern matches.
 *
 * The returned `showReference` flag is true when the message instructs the user
 * to quote a support reference code — callers should render the code when this is set.
 */
export function getContentSubmissionErrorMessage(
  error: Error | string | null | undefined,
): ContentSubmissionError {
  const rawMessage = error instanceof Error ? error.message : (error ?? "");
  const strippedMessage = stripErrorPrefixes(rawMessage);

  // Match against known patterns using the stripped message only.
  // Avoid matching raw message — it may contain JSON bodies with HTTP headers
  // (e.g. "permissions-policy") that trigger false positives.
  for (const { pattern, message } of CONTENT_ERROR_PATTERNS) {
    const match = pattern.exec(strippedMessage);
    if (match) {
      const msg = typeof message === "function" ? message(match) : message;
      return {
        message: msg,
        showReference: msg.includes("reference code shown below"),
      };
    }
  }

  // If the stripped message looks user-friendly (no technical jargon),
  // pass it through directly instead of showing a generic fallback
  if (
    strippedMessage.length > 0 &&
    strippedMessage.length < 200 &&
    !/graphql|internal.?server|stack.?trace|at\s+\w+\.\w+|node_modules/i.test(
      strippedMessage,
    )
  ) {
    return { message: strippedMessage, showReference: false };
  }

  // Generic fallback — actionable guidance without exposing raw technical details
  return {
    message:
      "Something went wrong while submitting your content. Please try again, and if the problem persists contact support with the reference code shown below.",
    showReference: true,
  };
}
